import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('Lien invalide ou expiré.');
      return;
    }
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Lien invalide ou expiré.');
        setLoading(false);
        return;
      }
      setDone(true);
    } catch (_) {
      setError('Une erreur est survenue. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center p-4"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl">
          {done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <i className="bi bi-check2-circle text-4xl text-emerald-400"></i>
              <h1 className="text-xl font-bold text-white mt-3">Mot de passe réinitialisé</h1>
              <p className="text-sm text-slate-300 mt-2">
                Votre mot de passe a été mis à jour. Vous pouvez maintenant vous connecter.
              </p>
              <a
                href="/login"
                className="inline-block mt-5 bg-[#0062CC] hover:bg-[#0056b3] text-white font-semibold py-3 px-6 rounded-xl transition"
                data-testid="reset-go-login"
              >
                Se connecter
              </a>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <i className="bi bi-shield-lock text-3xl text-blue-300"></i>
                <h1 className="text-xl font-bold text-white mt-3">Nouveau mot de passe</h1>
                <p className="text-sm text-slate-300 mt-2">
                  Choisissez un mot de passe d'au moins 10 caractères, différent de l'ancien.
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div
                    className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-200 text-sm flex items-center gap-2"
                    data-testid="reset-error"
                  >
                    <i className="bi bi-exclamation-triangle"></i>
                    {error}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-2">Nouveau mot de passe</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-300">
                      <i className="bi bi-lock"></i>
                    </span>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-xl py-3 pl-12 pr-12 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      placeholder="••••••••"
                      required
                      minLength={10}
                      data-testid="reset-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                      className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-300 hover:text-white transition"
                      data-testid="reset-password-toggle"
                    >
                      <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-2">Confirmer le mot de passe</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-300">
                      <i className="bi bi-lock-fill"></i>
                    </span>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-xl py-3 pl-12 pr-4 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      placeholder="••••••••"
                      required
                      minLength={10}
                      data-testid="reset-confirm"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0062CC] hover:bg-[#0056b3] text-white font-semibold py-3 px-4 rounded-xl transition disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <><i className="bi bi-arrow-repeat animate-spin"></i> Mise à jour...</>
                  ) : (
                    <>Réinitialiser le mot de passe</>
                  )}
                </button>
              </form>
              <div className="mt-6 text-center">
                <a
                  href="/login"
                  className="text-sm text-slate-300 hover:text-white underline-offset-2 hover:underline transition"
                  data-testid="reset-back-to-login"
                >
                  <i className="bi bi-arrow-left mr-1"></i> Retour à la connexion
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
